import { Component, forwardRef, HostBinding, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ColourOption, colourHex, colourLabel } from './colour-option';

/**
 * A dropdown of named colours with a swatch beside each, open and closed
 * (#226/#227). Was written out inline for a block's Background and Text
 * colour; a component now that the Panel grid needs four more (#264).
 *
 * Blank is a real choice ("nothing chosen") and stores nothing, so the clear
 * (x) is always offered. The closed field shows a stored value that is not
 * in `options` as its raw value, never as blank.
 */
@Component({
  selector: 'app-colour-select',
  templateUrl: './colour-select.component.html',
  styleUrls: ['./colour-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColourSelectComponent),
      multi: true,
    },
  ],
})
export class ColourSelectComponent implements ControlValueAccessor {
  /** Fills its grid cell, like the `w-full` dropdowns beside it. */
  @HostBinding('style.display') hostDisplay = 'block';

  @Input() options: ColourOption[] = [];
  @Input() placeholder = '';

  value: string | null = null;
  disabled = false;

  private onChange: (v: string | null) => void = () => {};
  onTouched: () => void = () => {};

  get selectedHex(): string {
    return colourHex(this.options, this.value);
  }

  get selectedLabel(): string {
    return colourLabel(this.options, this.value);
  }

  select(value: string | null): void {
    this.value = value ?? null;
    this.onChange(this.value);
  }

  writeValue(value: string | null): void {
    this.value = value ?? null;
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }
}
